import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Chip, IconButton, LinearProgress, alpha } from '@mui/material';
import { StarIcon } from 'lucide-react';

import type {
  DeleteOrganisationImageMutation,
  GetOrganisationDetailsImagesQuery,
  InsertOrganisationImagesBatchMutation,
} from '@graphql/__generated__/graphql';
import { NewImageGallery } from '@modules/_shared/components/image-gallery/NewImageGallery';
import { SMALL_CHIP_SX } from '@modules/_shared/components/image-gallery/styles/draggableStyles';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  DELETE_ORGANISATION_IMAGE,
  GET_ORGANISATION_IMAGES,
  INSERT_ORGANISATION_IMAGES_BATCH,
  SET_ORGANISATION_IMAGE_PRIMARY,
} from '@modules/organisations/organisationsQueries';
import { useLocale } from '@shared/hooks/locale';

type OrganisationImagesProps = {
  organisationId: string;
};

export const OrganisationImages: React.FC<OrganisationImagesProps> = ({
  organisationId,
}) => {
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data, loading } = useQuery<GetOrganisationDetailsImagesQuery>(
    GET_ORGANISATION_IMAGES,
    {
      variables: {
        organisationId,
      },
    },
  );

  const [insertImagesBatchMutation] =
    useMutation<InsertOrganisationImagesBatchMutation>(
      INSERT_ORGANISATION_IMAGES_BATCH,
      {
        onError: setErrorLike,
        update: (cache, { data: insertData }) => {
          if (!insertData?.insert_organisation_images?.returning) {
            return;
          }

          const newImages = insertData.insert_organisation_images.returning;

          cache.writeQuery({
            query: GET_ORGANISATION_IMAGES,
            variables: { organisationId },
            data: {
              organisation_images: [
                ...(data?.organisation_images ?? []),
                ...newImages,
              ],
            },
          });
        },
      },
    );
  const [deleteOrganisationImage] =
    useMutation<DeleteOrganisationImageMutation>(DELETE_ORGANISATION_IMAGE, {
      onError: setErrorLike,
      update: (cache, { data: deleteData }) => {
        if (!deleteData?.delete_organisation_images?.returning) {
          return;
        }

        const deletedImage = deleteData.delete_organisation_images.returning;

        deletedImage.forEach((image: { id: string }) => {
          cache.evict({ id: `organisation_images:${image.id}` });
        });
      },
    });
  const [makePrimaryImage] = useMutation(SET_ORGANISATION_IMAGE_PRIMARY, {
    onError: setErrorLike,
  });

  const galleryImages = useMemo(
    () =>
      data?.organisation_images.map((image, index) => ({
        id: image.id,
        order_nr: image.order_nr ?? index,
        is_primary: image.is_primary,
        canDelete: true,
        image: {
          id: image.image.id,
          url: image.image.url,
        },
      })) ?? [],
    [data?.organisation_images],
  );

  const handleDeleteImage = useCallback(
    async (imageId: string) => {
      const imageToDelete = galleryImages.find(image => image.id === imageId);

      if (!imageToDelete) {
        return;
      }

      await deleteOrganisationImage({
        variables: { id: imageToDelete.id, imageId: imageToDelete.image.id },
      });
    },
    [deleteOrganisationImage, galleryImages],
  );

  const uploadImages = useCallback(
    async (uploadedFiles: { id: string; url: string }[]) =>
      insertImagesBatchMutation({
        variables: {
          objects: uploadedFiles.map(file => ({
            organisation_id: organisationId,
            image: { data: { url: file.url, id: file.id } },
          })),
        },
      }),
    [insertImagesBatchMutation, organisationId],
  );

  if (loading) {
    return (
      <LinearProgress
        sx={{
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 2000,
        }}
      />
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <NewImageGallery
        images={galleryImages}
        onDeleteImage={handleDeleteImage}
        viewMode="grid"
        enableViewMode={false}
        disableRowSelection
        onFilesUploaded={uploadImages}
        renderCustomContent={image =>
          image.is_primary ? (
            <Chip
              icon={<StarIcon size={12} fill="currentColor" />}
              label={t('Active logo')}
              color="primary"
              size="small"
              sx={SMALL_CHIP_SX}
            />
          ) : (
            <IconButton
              size="small"
              color="primary"
              className="image-actions"
              sx={({ palette }) => ({
                opacity: 0,
                transition: 'opacity 0.2s',
                bgcolor: alpha(palette.white.main, 0.8),
                '&:hover': {
                  bgcolor: alpha(palette.white.main, 0.9),
                },
              })}
              onClick={e => {
                e.stopPropagation();
                makePrimaryImage({
                  variables: { id: image.id, organisationId },
                });
              }}
            >
              <StarIcon size={18} fill="currentColor" />
            </IconButton>
          )
        }
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Box>
  );
};
